import React, {
  useRef,
  useState,
  useImperativeHandle,
  forwardRef
} from 'react';
import { Button } from 'system/atoms';
import {
  ClickAwayListener,
  IconButton,
  Paper,
  Popper,
  Stack
} from '@mui/material';
import { Search } from '@mui/icons-material';
import { TextField } from 'common/form/components/TextField';

type TableV3SearchButtonProps = {
  isSorted?: boolean;
  placeholder?: string;
  columnTitle?: string;
  columnHeaderRef?: React.RefObject<HTMLDivElement>;
  onSearch: (value: string) => void;
};

export type TableV3SearchButtonRef = {
  openSearchPopup: () => void;
};

export const TableV3SearchButton = forwardRef<
  TableV3SearchButtonRef,
  TableV3SearchButtonProps
>(
  (
    { isSorted = false, placeholder, columnTitle, columnHeaderRef, onSearch },
    ref
  ): React.ReactElement => {
    const [open, setOpen] = useState(false);
    const [searchValue, setSearchValue] = useState('');
    const anchorRef = useRef<HTMLButtonElement>(null);

    // Use columnHeaderRef if provided, otherwise fall back to anchorRef
    const popperAnchorEl = columnHeaderRef?.current || anchorRef.current;

    const searchPlaceholder =
      placeholder ||
      (columnTitle ? `Search ${columnTitle.toLowerCase()}` : 'Search');

    useImperativeHandle(ref, () => ({
      openSearchPopup: () => setOpen(true)
    }));

    const handleClick = () => {
      setOpen((prev) => !prev);
    };

    const handleSearch = () => {
      const trimmedValue = searchValue.trim();
      if (trimmedValue) {
        onSearch(trimmedValue);
        setOpen(false);
      }
    };

    const handleKeyDown = (event: React.KeyboardEvent) => {
      if (event.key === 'Enter') {
        event.preventDefault();
        handleSearch();
      }
    };

    return (
      <>
        <IconButton
          ref={anchorRef}
          onClick={handleClick}
          sx={{
            color: isSorted ? 'customGrey.light' : 'customGrey.dark'
          }}
        >
          <Search />
        </IconButton>
        <Popper
          sx={{
            zIndex: 2
          }}
          open={open}
          anchorEl={popperAnchorEl}
          placement="bottom-start"
          modifiers={[
            {
              name: 'flip',
              enabled: false
            },
            {
              name: 'offset',
              options: {
                offset: [0, 0]
              }
            }
          ]}
        >
          <ClickAwayListener onClickAway={() => setOpen(false)}>
            <Paper
              elevation={1}
              sx={{
                p: '10px',
                minWidth: '440px',
                backgroundColor: 'white',
                border: 1,
                borderColor: '#28272C'
              }}
            >
              <Stack direction="row" gap="10px" alignItems="center">
                <TextField
                  name="search"
                  value={searchValue}
                  onChange={(value) => setSearchValue(value)}
                  field={{
                    autoFocus: true,
                    placeholder: searchPlaceholder,
                    startIcon: <Search fontSize="small" />,
                    onKeyDown: handleKeyDown
                  }}
                />
                <Button variant="tertiary" onClick={handleSearch}>
                  Search
                </Button>
              </Stack>
            </Paper>
          </ClickAwayListener>
        </Popper>
      </>
    );
  }
);
